import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldX, Globe, ChevronRight, CheckCircle2, XCircle, Loader2, MailWarning, Send, Clock } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatCard } from "@/components/common/StatCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { TooltipButton } from "@/components/common/Tooltips";
import { Panel2FAUsuarios } from "@/components/admin/Panel2FAUsuarios";
import PanelCamposBien from "@/components/admin/PanelCamposBien";
import { PanelCodigosRespaldo } from "@/components/admin/PanelCodigosRespaldo";
import { PanelProductoExtra } from "@/components/admin/PanelProductoExtra";
import { TableroCFDILectura } from "@/components/admin/TableroCFDILectura";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const SEM = {
  Verde: { cls: "bg-emerald-100 text-emerald-700" },
  Ambar: { cls: "bg-amber-100 text-amber-700" },
  Rojo: { cls: "bg-red-100 text-red-700" },
};

function SemBadge({ semaforo, vencidas }) {
  const m = SEM[semaforo] || SEM.Verde;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.cls}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {vencidas > 0 ? `${vencidas} vencido${vencidas > 1 ? "s" : ""}` : "Al día"}
    </span>
  );
}


export default function AdminSaas() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [fidMetricas, setFidMetricas] = useState({}); // entity_id -> {total, por_estado} (solo conteos, sin PII)
  const [error, setError] = useState(false);
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [rechazo, setRechazo] = useState(null); // {id, nombre}
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(null); // id de la entidad en proceso
  // feb-2026 iter 07-24l — Panel de admins de entidad varados (registro OK pero sin verificar
  // el correo). Detectados en la auditoría 07-24: 30 de 34 admin_entidad sin `email_verificado`,
  // 3 ya habían entrado con el token del registro y no pueden volver. Aquí el super_admin
  // puede reenviarles el correo sin marcarlos como verificados por él mismo (eso rompería
  // el control regulatorio).
  const [sinVerificar, setSinVerificar] = useState([]);
  const [expandirSinVerif, setExpandirSinVerif] = useState(false);
  const [reenviandoUid, setReenviandoUid] = useState(null);

  const loadSinVerificar = () =>
    api.get("/admin/entidades/admin-sin-verificar")
      .then((r) => setSinVerificar(r.data?.items || []))
      .catch(() => setSinVerificar([]));

  const load = () =>
    api.get("/admin/entidades").then((r) => setRows(r.data)).catch(() => setError(true));

  const loadFidMetricas = () =>
    api.get("/fideicomisos/admin/metricas")
      .then((r) => setFidMetricas(Object.fromEntries(r.data.map((m) => [m.entity_id, m]))))
      .catch(() => {});

  useEffect(() => {
    if (user?.role !== "super_admin") { setError(true); return; }
    load();
    loadFidMetricas();
    loadSinVerificar();
  }, [user]);

  const reenviarVerificacion = async (item) => {
    setReenviandoUid(item.user_id);
    try {
      const { data } = await api.post(`/admin/usuarios/${item.user_id}/reenviar-verificacion`);
      if (data?.correo_enviado) {
        toast.success(`Correo de verificación reenviado a ${item.email}.`);
      } else {
        toast.warning(`Registro actualizado, pero Brevo no confirmó el envío. Reintenta en 1 minuto.`);
      }
      loadSinVerificar();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setReenviandoUid(null);
    }
  };

  if (error)
    return (
      <div>
        <PageHeader title="Admin SaaS" subtitle="Panel de plataforma" />
        <div className="flex flex-col items-center justify-center gap-3 p-16 text-slate-400">
          <ShieldX className="h-10 w-10" />
          <p className="text-sm">Acceso exclusivo del Super Administrador de la plataforma.</p>
        </div>
      </div>
    );

  const totalMrr = rows.filter((r) => r.estatus_pago === "Al corriente").reduce((a, r) => a + Number(r.monto || 0), 0);
  const activas = rows.filter((r) => r.estatus === "Activa").length;
  const pendientes = rows.filter((r) => r.estatus === "pendiente_aprobacion").length;
  const usuarios = rows.reduce((a, r) => a + Number(r.usuarios_activos || 0), 0);

  const rowsFiltradas = soloPendientes ? rows.filter((r) => r.estatus === "pendiente_aprobacion") : rows;

  const goDetalle = (row) => navigate(`/entidades/${row.id}`);

  const autorizar = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/admin/solicitudes/${row.id}/aprobar`);
      toast.success(`Entidad ${row.nombre_comercial || row.razon_social} autorizada. El acceso quedó habilitado.`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  const abrirRechazo = (row) => { setRechazo({ id: row.id, nombre: row.nombre_comercial || row.razon_social }); setMotivo(""); };

  const confirmarRechazo = async () => {
    if (!motivo.trim()) { toast.error("Indica el motivo del rechazo"); return; }
    setBusy(rechazo.id);
    try {
      await api.post(`/admin/solicitudes/${rechazo.id}/rechazar`, { motivo, definitivo: false });
      toast.success("Solicitud rechazada. El representante podrá corregir y reenviar.");
      setRechazo(null); setMotivo("");
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div>
      <PageHeader title="Admin SaaS" subtitle="Cartera de entidades — suscripción, pagos y cumplimiento agregado (sin datos operativos)" />
      <div className="space-y-6 p-6 md:p-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <StatCard label="Entidades registradas" value={rows.length} icon="Building2" accent="primary" />
          <StatCard label="Entidades activas" value={activas} icon="CheckCircle2" accent="green" />
          <StatCard label="Pendientes de autorizar" value={pendientes} icon="Clock" accent="amber" />
          <StatCard label="MRR (al corriente)" value={mxn(totalMrr)} icon="Server" accent="primary" />
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            Usuarios activos totales: <span className="font-semibold text-slate-700">{usuarios}</span>
          </p>
          <Button data-testid="toggle-solo-pendientes" size="sm"
            variant={soloPendientes ? "default" : "outline"}
            className={soloPendientes ? "bg-amber-600 hover:bg-amber-700" : ""}
            onClick={() => setSoloPendientes((v) => !v)}>
            {soloPendientes ? "Ver todas las entidades" : `Ver solo pendientes de autorización (${pendientes})`}
          </Button>
        </div>

        <DataTable testid="table-admin-saas" rows={rowsFiltradas} onRowClick={goDetalle}
          columns={[
            { key: "nombre_comercial", label: "Entidad" },
            { key: "rfc", label: "RFC" },
            { key: "regimen", label: "Régimen", kind: "badge" },
            { key: "plan", label: "Plan" },
            { key: "estatus", label: "Estatus operativo",
              render: (r) => <StatusBadge value={r.estatus} /> },
            { key: "estatus_pago", label: "Pago", kind: "badge" },
            { key: "semaforo", label: "Cumplimiento", render: (r) => <SemBadge semaforo={r.semaforo} vencidas={r.obligaciones_vencidas} /> },
            { key: "fideicomisos", label: "Fideicomisos",
              render: (r) => {
                const m = fidMetricas[r.id];
                if (!m || !m.total) return <span className="text-xs text-slate-400">—</span>;
                const vig = m.por_estado?.vigente || 0;
                const eje = m.por_estado?.en_ejecucion || 0;
                return (
                  <span className="text-xs text-slate-700" data-testid={`fid-metricas-${r.id}`}>
                    <b>{m.total}</b>{vig ? ` · ${vig} vig.` : ""}{eje ? ` · ${eje} ejec.` : ""}
                    {m.en_rojo ? <span className="ml-1 font-semibold text-red-600">· {m.en_rojo} en rojo</span> : null}
                  </span>
                );
              } },
            { key: "monto", label: "Monto", kind: "currency" },
          ]}
          actions={(r) => (
            <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
              {r.estatus === "pendiente_aprobacion" && (
                <>
                  <TooltipButton tooltip="Autorizar el alta: habilita el acceso operativo de la entidad"
                    data-testid={`autorizar-btn-${r.id}`} size="sm"
                    disabled={busy === r.id}
                    onClick={() => autorizar(r)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700">
                    {busy === r.id ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="mr-1 h-3.5 w-3.5" />}
                    Autorizar
                  </TooltipButton>
                  <TooltipButton tooltip="Rechazar el alta con motivo (el representante podrá corregir)"
                    data-testid={`rechazar-btn-${r.id}`} size="sm" variant="outline"
                    disabled={busy === r.id}
                    onClick={() => abrirRechazo(r)}
                    className="border-red-200 text-red-600 hover:bg-red-50">
                    <XCircle className="mr-1 h-3.5 w-3.5" />
                    Rechazar
                  </TooltipButton>
                </>
              )}
              <Button data-testid={`resumen-btn-${r.id}`} size="sm" variant="outline" onClick={() => goDetalle(r)}>
                Ver ficha
              </Button>
            </div>
          )}
          emptyLabel={soloPendientes ? "No hay entidades pendientes de autorización." : "Sin entidades registradas"} />

        <button data-testid="link-centro-listas" onClick={() => navigate("/admin-listas")}
          className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10"><Globe className="h-5 w-5 text-primary" /></span>
            <div>
              <p className="font-heading text-sm font-semibold text-slate-900">Centro de Listas PLD</p>
              <p className="text-xs text-slate-500">Carga, versionado, frescura y re-escaneo de listas globales de sanciones.</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-slate-400" />
        </button>

        {/* jun-2026 — Tablero de LECTURA de producción CFDI + Carta Manifiesto. El
            encendido operativo y la carta se operan en la ficha de cada entidad
            (/entidades/:id). Aquí solo contadores y consulta; sin botones de acción. */}
        <TableroCFDILectura />

        {/* ago-2026 — Solicitudes de producto de crédito extra ($500/mes + IVA por
            transferencia). Aprobar aquí amplía el cupo de la entidad; Stripe no participa. */}
        <PanelProductoExtra />

        {/* 2FA. Gestión del segundo factor de toda la plataforma y códigos de respaldo del
            propio super_admin. ago-2026 · iter127 — toda la configuración (interruptor
            general y los dos interruptores por rol) vive en base de datos y se maneja desde
            aquí, con efecto inmediato y sin variables de entorno ni redespliegue. */}
        <PanelCodigosRespaldo />
        <Panel2FAUsuarios />

        {/* ago-2026 — Campos del BIEN por actividad vulnerable: el formulario de alta del
            bien de cada una de las 17 actividades se arma con esta lista. */}
        <PanelCamposBien />

        {/* Panel: administradores de entidad con correo sin verificar (varados post-registro).
            El super_admin puede reenviarles el correo desde aquí. NO los marca verificados. */}
        <section className="rounded-lg border border-amber-200 bg-white shadow-sm" data-testid="sin-verificar-panel">
          <button type="button"
            onClick={() => setExpandirSinVerif((v) => !v)}
            className="flex w-full items-center justify-between p-5 text-left"
            data-testid="sin-verificar-toggle">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100">
                <MailWarning className="h-5 w-5 text-amber-600" />
              </span>
              <div>
                <p className="font-heading text-sm font-semibold text-slate-900">
                  Administradores sin verificar su correo
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800"
                        data-testid="sin-verificar-count">
                    {sinVerificar.length}
                  </span>
                </p>
                <p className="text-xs text-slate-500">
                  Cuentas de entidad que se registraron pero aún no confirmaron su correo. Reenvía el enlace desde aquí — no marca a nadie como verificado.
                </p>
              </div>
            </div>
            <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${expandirSinVerif ? "rotate-90" : ""}`} />
          </button>
          {expandirSinVerif && (
            <div className="border-t border-amber-100 p-4" data-testid="sin-verificar-list">
              {sinVerificar.length === 0 ? (
                <p className="py-6 text-center text-sm text-slate-500">No hay administradores varados.</p>
              ) : (
                <div className="max-h-96 space-y-2 overflow-y-auto">
                  {sinVerificar.map((it) => (
                    <div key={it.user_id}
                         className="flex flex-col gap-1.5 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs md:flex-row md:items-center md:justify-between"
                         data-testid={`sin-verificar-item-${it.user_id}`}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-semibold text-slate-900" data-testid={`sin-verificar-email-${it.user_id}`}>
                          {it.email}
                        </p>
                        <p className="truncate text-slate-500">
                          {it.name || "—"} · {it.entidad?.razon_social || "sin razón social"}
                          {it.entidad?.estatus ? <span className="ml-1 text-slate-400">· estatus {it.entidad.estatus}</span> : null}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500">
                          <span>Alta: {String(it.created_at || "").slice(0, 10) || "—"}</span>
                          {it.ultimo_acceso ? (
                            <span className="text-red-600" data-testid={`sin-verificar-varado-${it.user_id}`}>
                              ⚠ Ya entró vía token de registro (varado)
                            </span>
                          ) : null}
                          {it.verify_token?.existe ? (
                            it.verify_token.vencido ? (
                              <span className="flex items-center gap-0.5 text-amber-700">
                                <Clock className="h-3 w-3" /> token vencido
                              </span>
                            ) : it.verify_token.usado ? (
                              <span>token ya usado</span>
                            ) : (
                              <span>token vigente</span>
                            )
                          ) : (
                            <span className="text-slate-400">sin token emitido</span>
                          )}
                        </p>
                      </div>
                      <TooltipButton
                        tooltip="Reenvía el correo de verificación al usuario. NO lo marca como verificado."
                        data-testid={`sin-verificar-reenviar-${it.user_id}`}
                        size="sm" variant="outline"
                        disabled={reenviandoUid === it.user_id}
                        className="border-amber-300 text-amber-800 hover:bg-amber-50"
                        onClick={() => reenviarVerificacion(it)}>
                        {reenviandoUid === it.user_id
                          ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          : <Send className="mr-1.5 h-3.5 w-3.5" />}
                        Reenviar correo
                      </TooltipButton>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <Dialog open={!!rechazo} onOpenChange={(v) => { if (!v) { setRechazo(null); setMotivo(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="rechazo-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rechazar solicitud de alta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <p className="text-sm text-slate-600">
              Entidad: <span className="font-semibold text-slate-800">{rechazo?.nombre}</span>
            </p>
            <p className="text-xs text-slate-500">
              El motivo se registra en bitácora y se muestra al representante para que corrija y reenvíe.
            </p>
            <Textarea data-testid="rechazo-motivo" rows={4}
              placeholder="Ej. La constancia fiscal no corresponde al RFC declarado."
              value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRechazo(null); setMotivo(""); }}>Cancelar</Button>
            <Button data-testid="rechazo-confirmar" onClick={confirmarRechazo}
              disabled={busy === rechazo?.id || !motivo.trim()}
              className="bg-red-600 hover:bg-red-700 text-white">
              {busy === rechazo?.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <XCircle className="mr-1.5 h-4 w-4" />}
              Rechazar solicitud
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
